import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  VideoCamera, VideoCameraSlash, Plugs, Warning, MagicWand, Sliders, Mountains
} from "@phosphor-icons/react";
import { toast } from "sonner";
import useVirtualBackground from "@/hooks/useVirtualBackground";
import api from "@/lib/api";

// Camera enhancement presets — boosts contrast/brightness/sharpness for low-quality webcams.
const PRESETS = {
  off:   { brightness: 1.00, contrast: 1.00, saturate: 1.00, sharpness: 0, label: "Off" },
  soft:  { brightness: 1.05, contrast: 1.10, saturate: 1.10, sharpness: 0.3, label: "Doux" },
  studio:{ brightness: 1.08, contrast: 1.20, saturate: 1.25, sharpness: 0.6, label: "Studio" },
  cinema:{ brightness: 1.12, contrast: 1.35, saturate: 1.35, sharpness: 0.9, label: "Cinéma" },
};

const BACKGROUNDS = [
  { id: "off", label: "Aucun", kind: "off" },
  { id: "blur", label: "Flou", kind: "blur", blur: 14 },
  { id: "paris", label: "Paris nuit", kind: "image",
    imageUrl: "https://images.pexels.com/photos/161853/eiffel-tower-paris-france-tower-161853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=720&w=1280" },
  { id: "tokyo", label: "Tokyo néon", kind: "image",
    imageUrl: "https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=720&w=1280" },
  { id: "stage", label: "Scène", kind: "image",
    imageUrl: "https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=720&w=1280" },
  { id: "galaxy", label: "Galaxie", kind: "image",
    imageUrl: "https://images.pexels.com/photos/957061/milky-way-starry-sky-night-sky-star-957061.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=720&w=1280" },
];

const ArtistCameraStage = ({ active, onActiveChange, concertId }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState("studio");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [custom, setCustom] = useState({ brightness: 108, contrast: 120, saturate: 125, sharpness: 60 });
  const [resInfo, setResInfo] = useState(null);
  const [bgId, setBgId] = useState("off");
  const currentBg = BACKGROUNDS.find((b) => b.id === bgId) || BACKGROUNDS[0];
  const { canvasRef, status: bgStatus } = useVirtualBackground({
    videoRef,
    enabled: active && currentBg.kind !== "off",
    background: currentBg,
  });

  const loadDevices = async () => {
    try {
      const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      tmp.getTracks().forEach((t) => t.stop());
      const list = await navigator.mediaDevices.enumerateDevices();
      const cams = list.filter((d) => d.kind === "videoinput");
      setDevices(cams);
      if (cams.length && !selectedId) setSelectedId(cams[0].deviceId);
    } catch (e) {
      setError(e.message || "Accès caméra refusé");
    }
  };

  const startCamera = async (deviceId) => {
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      // Request the BEST possible resolution; browser falls back to what device supports
      const constraints = {
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 480 },
          frameRate: { ideal: 30, min: 15 },
        },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      setResInfo(`${settings.width || "?"}×${settings.height || "?"} @ ${settings.frameRate || "?"}fps`);
      onActiveChange?.(true);
      setError(null);
    } catch (e) {
      setError(e.message || "Impossible d'ouvrir la caméra");
      toast.error("Caméra inaccessible : " + (e.message || "vérifiez vos autorisations"));
      onActiveChange?.(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    onActiveChange?.(false);
    setResInfo(null);
  };

  // Stream snapshots to backend every 2s (MVP — replace by WebRTC later)
  useEffect(() => {
    if (!active || !concertId) return;
    const interval = setInterval(() => {
      try {
        const src = currentBg.kind !== "off" ? canvasRef.current : videoRef.current;
        if (!src) return;
        const w = src.videoWidth || src.width || 1280;
        const h = src.videoHeight || src.height || 720;
        if (!w || !h) return;
        const cap = document.createElement("canvas");
        const scale = Math.min(1, 960 / w);  // downscale to ~960px wide
        cap.width = Math.floor(w * scale);
        cap.height = Math.floor(h * scale);
        const ctx = cap.getContext("2d");
        ctx.drawImage(src, 0, 0, cap.width, cap.height);
        const dataUrl = cap.toDataURL("image/jpeg", 0.6);
        api.post(`/concerts/${concertId}/snapshot`, { data_url: dataUrl }).catch(() => {});
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("snapshot upload failed", err);
      }
    }, 2000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, concertId, currentBg.kind]);

  useEffect(() => {
    loadDevices();
    return () => stopCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (active && selectedId) startCamera(selectedId);
    if (!active) stopCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selectedId]);

  // Build CSS filter string (CSS filters run in GPU - no perf cost)
  const p = showAdvanced
    ? { brightness: custom.brightness/100, contrast: custom.contrast/100, saturate: custom.saturate/100, sharpness: custom.sharpness/100 }
    : PRESETS[preset];
  const cssFilter = `brightness(${p.brightness}) contrast(${p.contrast}) saturate(${p.saturate}) ${p.sharpness ? `url(#xookmedia-sharpen)` : ""}`;

  return (
    <div className="absolute inset-0 z-20 flex flex-col">
      {/* SVG sharpening convolution filter for low-quality cams */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
        <filter id="xookmedia-sharpen">
          <feConvolveMatrix
            order="3 3"
            kernelMatrix={`0 ${-1 * p.sharpness} 0  ${-1 * p.sharpness} ${1 + 4 * p.sharpness} ${-1 * p.sharpness}  0 ${-1 * p.sharpness} 0`}
          />
        </filter>
      </svg>

      <video
        ref={videoRef}
        data-testid="artist-camera-preview"
        autoPlay
        playsInline
        muted
        className={`w-full h-full object-cover bg-black ${active && currentBg.kind === "off" ? "opacity-100" : "opacity-0 absolute pointer-events-none"}`}
        style={{ filter: active && currentBg.kind === "off" ? cssFilter : undefined }}
      />
      {/* Virtual-background canvas (shown when bg is on) */}
      <canvas
        ref={canvasRef}
        data-testid="artist-camera-canvas"
        className={`w-full h-full object-cover bg-black ${active && currentBg.kind !== "off" ? "opacity-100" : "opacity-0 absolute pointer-events-none"}`}
        style={{ filter: active && currentBg.kind !== "off" ? cssFilter : undefined }}
      />

      {!active && (
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <div>
            <Plugs size={48} className="text-[#F59E0B] mx-auto mb-3" weight="duotone" />
            <p className="text-zinc-300 font-bold mb-1">Connectez votre caméra USB</p>
            <p className="text-zinc-500 text-sm">Branchez votre webcam, puis cliquez sur Activer.</p>
          </div>
        </div>
      )}

      {/* Top controls */}
      <div className="absolute top-3 left-3 right-3 flex flex-wrap items-center gap-2 z-30">
        <div className="flex-1 min-w-[140px] max-w-xs">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="artist-camera-select" className="bg-black/70 border-white/20 text-white text-xs h-8">
              <SelectValue placeholder="Aucune caméra détectée" />
            </SelectTrigger>
            <SelectContent>
              {devices.length === 0 && <SelectItem value="none" disabled>Aucune caméra</SelectItem>}
              {devices.map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId}>
                  {d.label || `Caméra ${d.deviceId.slice(0, 6)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="artist-camera-toggle" size="sm"
          onClick={() => onActiveChange(!active)}
          className={active ? "bg-red-600 hover:bg-red-700" : "bg-[#10B981] hover:bg-[#059669]"}>
          {active ? <><VideoCameraSlash className="mr-1" /> Couper</> : <><VideoCamera className="mr-1" /> Activer</>}
        </Button>
        <Button data-testid="artist-camera-refresh" size="sm" variant="outline"
          className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={loadDevices}>
          Rafraîchir
        </Button>
      </div>

      {/* Enhancement controls (visible only when active) */}
      {active && (
        <div className="absolute bottom-3 left-3 right-3 z-30 space-y-2">
          {/* Virtual background row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-black/70 border border-white/10 text-xs">
              <Mountains size={14} className="text-[#F59E0B]" weight="fill" />
              <span className="uppercase tracking-widest text-zinc-400">Fond IA</span>
            </div>
            {BACKGROUNDS.map((b) => (
              <Button
                key={b.id}
                data-testid={`cam-bg-${b.id}`}
                size="sm"
                variant={bgId === b.id ? "default" : "outline"}
                onClick={() => setBgId(b.id)}
                className={bgId === b.id ? "bg-[#F59E0B] hover:bg-[#D97706] text-black h-7 px-3 text-xs" : "border-white/20 text-white hover:bg-white/10 hover:text-white h-7 px-3 text-xs"}
              >
                {b.label}
              </Button>
            ))}
            {currentBg.kind !== "off" && (
              <span className="text-xs text-zinc-500 font-mono ml-auto">
                Segmentation : {bgStatus === "ready" ? "✓ live" : bgStatus === "loading" ? "chargement…" : bgStatus === "error" ? "indispo" : "—"}
              </span>
            )}
          </div>

          {/* Boost row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-black/70 border border-white/10 text-xs">
              <MagicWand size={14} className="text-[#E11D48]" weight="fill" />
              <span className="uppercase tracking-widest text-zinc-400">Boost IA</span>
            </div>
            {Object.entries(PRESETS).map(([key, val]) => (
              <Button
                key={key}
                data-testid={`cam-preset-${key}`}
                size="sm"
                variant={!showAdvanced && preset === key ? "default" : "outline"}
                onClick={() => { setShowAdvanced(false); setPreset(key); }}
                className={!showAdvanced && preset === key ? "bg-[#E11D48] hover:bg-[#BE123C] text-white h-7 px-3 text-xs" : "border-white/20 text-white hover:bg-white/10 hover:text-white h-7 px-3 text-xs"}
              >
                {val.label}
              </Button>
            ))}
            <Button
              data-testid="cam-advanced-toggle"
              size="sm"
              variant={showAdvanced ? "default" : "outline"}
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={showAdvanced ? "bg-[#F59E0B] hover:bg-[#D97706] text-black h-7 px-3 text-xs" : "border-white/20 text-white hover:bg-white/10 hover:text-white h-7 px-3 text-xs"}
            >
              <Sliders size={12} className="mr-1" /> Avancé
            </Button>
            {resInfo && <span className="ml-auto text-xs text-zinc-500 font-mono">{resInfo}</span>}
          </div>

          {showAdvanced && (
            <div className="rounded-lg bg-black/80 border border-white/10 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { k: "brightness", label: "Luminosité", min: 50, max: 200 },
                { k: "contrast", label: "Contraste", min: 50, max: 200 },
                { k: "saturate", label: "Couleur", min: 0, max: 200 },
                { k: "sharpness", label: "Netteté IA", min: 0, max: 150 },
              ].map((s) => (
                <div key={s.k}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-zinc-400">{s.label}</span>
                    <span className="font-mono text-zinc-300">{custom[s.k]}</span>
                  </div>
                  <Slider
                    data-testid={`cam-slider-${s.k}`}
                    value={[custom[s.k]]} min={s.min} max={s.max} step={1}
                    onValueChange={(v) => setCustom((c) => ({ ...c, [s.k]: v[0] }))}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="absolute top-14 left-3 right-3 p-3 rounded-lg bg-red-950/80 border border-red-500/30 text-xs text-red-200 flex items-center gap-2 z-30">
          <Warning weight="fill" /> {error}
        </div>
      )}
    </div>
  );
};

export default ArtistCameraStage;
